import { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLegacyReportReview } from "../../../hooks/useLegacyReportReview";
import { legacyReportsService } from "../../../services/legacyReports.service";
import LegacyReportWorkspace from "../../../components/admin/legacy-reports/LegacyReportWorkspace";
import LegacyReportOriginal from "../../../components/admin/legacy-reports/LegacyReportOriginal";
import { useAuthStore } from "../../../store";
import { canManageLegacyCases } from "../../../utils/legacyCaseAccess";
import LegacyTechnicalReportNotice from "../../../components/admin/legacy-cases/LegacyTechnicalReportNotice";

export default function LegacyReportReviewPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const query = useLegacyReportReview(caseId);
  const profile = useAuthStore((state) => state.profile);
  const client = useQueryClient();
  const prepare = useMutation({
    mutationFn: () => legacyReportsService.prepare(caseId!),
    onSuccess: (state) =>
      client.setQueryData(["legacy-report-review", caseId], state),
  });
  const handlePrepare = useCallback(() => {
    prepare.mutate();
  }, [prepare]);
  const handleRetry = useCallback(() => {
    void query.refetch();
  }, [query]);
  if (query.isLoading)
    return (
      <Skeleton
        height={600}
        role="status"
        aria-label="Loading historical reconstruction"
      />
    );
  if (!query.data || query.error)
    return (
      <Alert color="red" title="Report reconstruction unavailable">
        <Stack>
          <Text>
            {query.error?.message ||
              "Open an imported case to review its historical report."}
          </Text>
          <Button onClick={handleRetry}>Retry reconstruction</Button>
          <Anchor component={Link} to={caseId ? `/cases/${caseId}` : "/cases"}>
            Back to case
          </Anchor>
        </Stack>
      </Alert>
    );
  if (query.data.reportKind === "technical")
    return (
      <Stack gap="lg">
        <Title order={1}>Original NovoPath Technical Summary</Title>
        <LegacyTechnicalReportNotice caseId={query.data.caseId} />
        <LegacyReportOriginal source={query.data.source} />
      </Stack>
    );
  return (
    <Stack gap="lg">
      <Breadcrumbs>
        {canManageLegacyCases(profile) && (
          <Anchor component={Link} to="/admin/legacy-cases">
            Legacy Cases
          </Anchor>
        )}
        <Anchor component={Link} to={`/cases/${caseId}`}>
          Imported case
        </Anchor>
        <Text>Report reconstruction</Text>
      </Breadcrumbs>
      <Title order={1}>Historical report reconstruction</Title>
      {query.data.draft ? (
        <LegacyReportWorkspace
          key={query.data.caseId}
          initial={{ ...query.data, draft: query.data.draft }}
        />
      ) : (
        <Stack gap="md">
          <Alert color="blue" title="Prepare the report for fidelity review">
            Preparation creates an editable reconstruction from the preserved
            source. Compare its literal content, signatures, and graphics before
            approval.
          </Alert>
          {prepare.error && (
            <Alert color="red" title="Preparation unavailable">
              {prepare.error.message}
            </Alert>
          )}
          {query.data.capabilities.canPrepare ? (
            <Button onClick={handlePrepare} loading={prepare.isPending}>
              Prepare reconstruction
            </Button>
          ) : (
            <Alert color="orange">
              An authorized administrator or medical director must prepare this
              reconstruction.
            </Alert>
          )}
          <LegacyReportOriginal source={query.data.source} />
        </Stack>
      )}
    </Stack>
  );
}
